import {ColumnDef, FilterDef, FilterOperator, FilterType, ListRequestIdModelTable} from "../../components/ExTable";
import {useTranslation} from "react-i18next";
import {
    OrganizationCreateRequest,
    useOrganizationCreateMutation,
    useOrganizationListQuery,
    useStageListQuery,
} from "../../app/api/api";
import {Organization, OrganizationField} from "../../app/api/model/organization";
import {formatTimestamp} from "../../utils/datetime";
import {IconButton, Tooltip} from "@mui/material";
import CreateIcon from "@mui/icons-material/AddCircleOutline";
import {getTranslation, TranslationContext} from "../../utils/getTranslationKey";
import {ordinaryDetailPageLinkTo} from "../../utils/ordinaryDetailPageLink";
import {RoutePath} from "../../types";
import {CompanyCardField} from "../../app/api/model/organization/companyCard";
import React, {useState} from "react";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import {useModal} from "../../hooks/useModal";
import {AddEditCompanyCard} from "./AddEditCompanyCard";
import {EMPTY_UI} from "../../variables";
import UpdateDialog from "../../components/Dialog/UpdateDialog";
import {getCreateOrganizationValidationSchema} from "./Detail/validation";
import {InputFieldType} from "../../components/input/factory/types";
import {PermissionLevel} from "../../app/api/model/user";
import {usePermission} from "../../hooks/usePermission";
import Page from "../../components/Layout/Page";

export default function OrganizationTable() {
    const {t} = useTranslation();
    const [selectedOrganization, setSelectedOrganization] = useState<Organization | undefined>();
    const addCompanyCardModal = useModal(false);
    const {data: stageListResponse} = useStageListQuery({});
    const createOrganizationModal = useModal(false);
    const [createOrganization] = useOrganizationCreateMutation();
    const {hasPermission} = usePermission();

    const openAddCompanyCardModal = (organization: Organization, e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        setSelectedOrganization(organization);
        addCompanyCardModal.setIsOpen(true);
    };
    const onAddEditCompanyCardClose = () => {
        setSelectedOrganization(undefined);
    };

    const onSubmitOrganization = async (requestData: Partial<Organization>) => {
        const createData = {
            name: requestData?.name,
            country: requestData?.country,
            adminEmail: requestData?.adminEmail,
            adminLanguage: requestData?.adminLanguage,
            productiveStage: requestData?.productiveStage,
            ...(requestData?.mainDKVCustomerNumber ? {mainDKVCustomerNumber: requestData?.mainDKVCustomerNumber} : {}),
        };
        await createOrganization(createData as OrganizationCreateRequest).unwrap();
    };

    // add on close to unser selected Org
    const columnDefs: ColumnDef<Organization>[] = [
        {
            fieldName: OrganizationField.ID,
            align: "right",
            label: t("generic_id"),
            width: 120,
            sortable: true,
            generateLink: ordinaryDetailPageLinkTo(RoutePath.ORGANIZATIONS),
            sticky: true,
        },
        {
            fieldName: OrganizationField.NAME,
            align: "left",
            label: t("organizations_name"),
            width: 200,
            sortable: true,
            generateLink: ordinaryDetailPageLinkTo(RoutePath.ORGANIZATIONS),
        },
        {
            fieldName: OrganizationField.COUNTRY,
            align: "left",
            label: t("generic_country"),
            width: 150,
            sortable: true,
            formatCellText: ({cellValue}) => {
                return getTranslation({context: TranslationContext.COUNTRY, value: cellValue, t});
            },
        },
        {
            fieldName: OrganizationField.MAIN_DKV_CUSTOMER_NUMBER,
            align: "left",
            label: t("organizations_mainDKVCustomerNumber"),
            width: 300,
            sortable: true,
        },
        {
            fieldName: OrganizationField.COMPANY_CARD,
            align: "left",
            label: t("generic_companyCard"),
            width: 300,
            sortable: false,
            formatCellText: ({cellValue, row}) => {
                return cellValue
                    ? cellValue[CompanyCardField.CARD_ID]
                    : {
                          title: t("tooltip_addCompanyCard"),
                          node: (
                              <Tooltip title={t("tooltip_addCompanyCard")} key={`addCompanyCard.${row.id}`}>
                                  <IconButton onClick={(e) => openAddCompanyCardModal(row, e)} size={"small"}>
                                      <AddCircleIcon />
                                  </IconButton>
                              </Tooltip>
                          ),
                      };
            },
        },
        {
            fieldName: OrganizationField.PRODUCTIVE_STAGE,
            align: "left",
            label: t("organizations_productiveStage"),
            width: 200,
            sortable: true,
            formatCellText: ({row}) => {
                return (
                    stageListResponse?.payload?.find((s) => s.identifier === row.productiveStage)?.description ||
                    row.productiveStage ||
                    EMPTY_UI
                );
            },
        },
        {
            fieldName: OrganizationField.ALL_STAGES,
            align: "left",
            label: t("organizations_allStages"),
            width: 300,
            sortable: true,
            formatCellText: ({row}) => {
                return (
                    row.allStages
                        ?.split(",")
                        .map(
                            (se) =>
                                stageListResponse?.payload?.find((s) => s.identifier === se.trim())?.description ??
                                se.trim()
                        )
                        .join(", ") ?? EMPTY_UI
                );
            },
        },
        {
            fieldName: OrganizationField.LOCKED,
            align: "left",
            label: t("organizations_globalLocked"),
            width: 200,
            sortable: true,
            formatCellText: ({cellValue}) => {
                return Number(cellValue) ? t("generic_yes") : t("generic_no");
            },
        },
        {
            fieldName: OrganizationField.LOCKED_AT,
            align: "left",
            label: t("organizations_lockedAt"),
            width: 150,
            sortable: true,
            formatCellText: ({cellValue}) => {
                return formatTimestamp(cellValue);
            },
        },
        {
            fieldName: OrganizationField.CREATED_AT,
            align: "left",
            label: t("generic_createdAt"),
            width: 150,
            sortable: true,
            formatCellText: ({cellValue}) => {
                return formatTimestamp(cellValue);
            },
        },
    ];
    const filterDefs: FilterDef<Organization>[] = [
        {
            fieldName: OrganizationField.NAME,
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.EQ, FilterOperator.STARTSWITH],
        },
        {
            fieldName: OrganizationField.ID,
            filterOperators: [
                FilterOperator.EQ,
                FilterOperator.LT,
                FilterOperator.GT,
                FilterOperator.LTE,
                FilterOperator.GTE,
            ],
        },
        {
            fieldName: OrganizationField.COUNTRY,
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ],
        },
        {
            fieldName: OrganizationField.LOCKED,
            filterOperators: [FilterOperator.EQ, FilterOperator.NOT],
            type: FilterType.ENUM,
            enumValues: [
                {
                    value: "0",
                    labelKey: t("generic_no"),
                },
                {
                    value: "1",
                    labelKey: t("generic_yes"),
                },
            ],
        },
        {
            fieldName: OrganizationField.MAIN_DKV_CUSTOMER_NUMBER,
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ],
        },
        {
            fieldName: OrganizationField.PRODUCTIVE_STAGE,
            filterOperators: [FilterOperator.EQ, FilterOperator.NOT],
            type: FilterType.ENUM,
            enumValues:
                stageListResponse?.payload?.map((s) => {
                    return {value: s.identifier, labelKey: s.description};
                }) ?? [],
        },
        {
            fieldName: OrganizationField.LOCKED_AT,
            filterOperators: [
                FilterOperator.EQ,
                FilterOperator.LT,
                FilterOperator.GT,
                FilterOperator.LTE,
                FilterOperator.GTE,
            ],
            type: FilterType.DATE,
        },
        {
            fieldName: OrganizationField.CREATED_AT,
            filterOperators: [
                FilterOperator.EQ,
                FilterOperator.LT,
                FilterOperator.GT,
                FilterOperator.LTE,
                FilterOperator.GTE,
            ],
            type: FilterType.DATE,
        },
    ];

    return (
        <Page>
            <ListRequestIdModelTable<Organization>
                dataTest="organizationsList"
                queryHook={useOrganizationListQuery}
                columnDefs={columnDefs}
                filterDefs={filterDefs}
                title={t("generic_organizations")}
                renderActions={(keys) => (
                    <>
                        {keys.length === 0 && hasPermission(PermissionLevel.MANAGER) && (
                            <Tooltip title={t("action_createOrganization")}>
                                <IconButton onClick={createOrganizationModal.toggleIsOpen}>
                                    <CreateIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                    </>
                )}
            />
            {selectedOrganization && (
                <AddEditCompanyCard
                    modal={addCompanyCardModal}
                    organization={selectedOrganization}
                    onClose={onAddEditCompanyCardClose}
                />
            )}
            <UpdateDialog
                modal={createOrganizationModal}
                titleKey={"action_createOrganization"}
                validationSchema={getCreateOrganizationValidationSchema}
                onSubmit={onSubmitOrganization}
                initialValues={{
                    name: "",
                    country: "",
                    adminEmail: "",
                    adminLanguage: "",
                    productiveStage: "",
                    mainDKVCustomerNumber: "",
                }}
                inputFields={[
                    {
                        translationKey: "organizations_name",
                        fieldName: OrganizationField.NAME,
                        required: true,
                    },
                    {
                        translationKey: "organizations_mainDKVCustomerNumber",
                        fieldName: OrganizationField.MAIN_DKV_CUSTOMER_NUMBER,
                    },
                    {
                        translationKey: "organizations_adminEmail",
                        fieldName: OrganizationField.ADMIN_EMAIL,
                        required: true,
                    },
                    {
                        translationKey: "generic_country",
                        fieldName: OrganizationField.COUNTRY,
                        type: InputFieldType.COUNTRY,
                        required: true,
                    },
                    {
                        translationKey: "organizations_adminLanguage",
                        fieldName: OrganizationField.ADMIN_LANGUAGE,
                        type: InputFieldType.LANGUAGE,
                        required: true,
                    },
                    {
                        translationKey: "organizations_productiveStage",
                        fieldName: OrganizationField.PRODUCTIVE_STAGE,
                        required: true,
                        type: InputFieldType.STAGE_SELECT,
                        fieldProps: {requestParams: {onlyProductive: true, showTotal: true}},
                    },
                ]}
            />
        </Page>
    );
}
